import { Subject } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import {
  ICustomerForm,
  IServiceRequestForm,
  IVehicleForm,
  ServiceAppointmentState
} from '../../store/service-appointment/service-appointment.reducer';
import { vehicleFormToVehicleProjector, getTotalCostAndLabourHrs } from '../../store/service-appointment/service-appointment.adapter';
import { AppointmentBookingSource, AppointmentType, IAddress, ICustomer, IServiceAppointment, IServiceRequest, TransportationType } from '@signal/asp-data-commons';
import {Duration} from 'luxon';
import { countryCd } from '../../shared/services/util.service';
import { VehicleService } from '../../shared/services/vehicle.service';
import { CustomerType } from '../../shared/enums/customer-types.enum';

@Injectable({
  providedIn: 'root'
})
export class AppointmentFormService {

  /* Service main form - Accessible from outside */
  appointmentMainForm: UntypedFormGroup;
  public validateServices: Subject<void> = new Subject();
  /* DetailMap for showing summary */
  detailMap: { [key: string]: string };
  // JSON to store the session details before login
  sessionValue = {};
  isEditMode = false;
  /* All 4 steps are sub form - Accessible only through the main form */
  private readonly selectServiceForm: UntypedFormGroup;
  private readonly selectVehicleForm: UntypedFormGroup;
  private readonly appointmentTimeForm: UntypedFormGroup;
  private readonly yourInformationForm: UntypedFormGroup;
  private isOpcodeCompRentalEligible: boolean[] = [];
  private costForCompEligible = "";
  private flatRateHrsForCompEligible = "";

  constructor(
    private readonly vehicleService: VehicleService,
  ) {
  }
  getMainFormDataAsCreateReq(serviceAppointmentFeature: ServiceAppointmentState): IServiceAppointment {
    const appointmentForm = serviceAppointmentFeature.appointmentForm;
    const availability = serviceAppointmentFeature.availability;
    const { countryCd, languageCd } = this.vehicleService.getCountryAndLang();
    const appointmentDetails = {} as IServiceAppointment;
    appointmentDetails.dealerLanguageCd = languageCd;
    appointmentDetails.dealerCountryCd = countryCd;
    appointmentDetails.appointmentBookingSource = AppointmentBookingSource.ASPBDC;

    appointmentDetails.emailAddress = appointmentForm.customerForm.email || "";
    if(!appointmentDetails.emailAddress) {
      delete appointmentDetails.emailAddress;
    }
    appointmentDetails.appointmentType = AppointmentType.SERVICE;
    if (this.checkIfRecallServiceSelected(appointmentForm.serviceForm)) {
      appointmentDetails.recallCodes = appointmentForm.recallCodes || [];
    }

    appointmentDetails.languageId = appointmentForm.availabilityForm.availability.languageId || "";
    appointmentDetails.advisorId = appointmentForm.availabilityForm.availability.advisorId || "";
    appointmentDetails.appointmentStartDate = appointmentForm.availabilityForm.availability.appointmentStartDate || "";
    appointmentDetails.transportationTypeCode = appointmentForm.customerForm.transport || TransportationType.WAITER ;
    appointmentDetails.appointmentComments = appointmentForm.customerForm.appointmentComments || "";
    if (appointmentDetails.transportationTypeCode === 'REMOTE' || appointmentDetails.transportationTypeCode === 'PICKUP' ||
      appointmentDetails.transportationTypeCode === 'DELIVERY') {


      for (let i = 0; i < availability.data.availableDates.length; i++) {
        if (availability.data.availableDates[i].date === appointmentForm.availabilityForm.availability.appointmentStartDate) {
          for (let j = 0; j < availability.data.availableDates[i].timeSlots.length; j++) {
            if (availability.data.availableDates[i].timeSlots[j].timeSlotId === appointmentForm.availabilityForm.availability.timeSlotId) {
              if (availability.data.availableDates[i].timeSlots[j].remoteEstimatedCompletionTime) {
                //appointmentDetails.remoteEstimatedCompletionTime = availability.data.availableDates[i].timeSlots[j].remoteEstimatedCompletionTime;

                switch (appointmentDetails.transportationTypeCode) {
                  case 'PICKUP':
                    {
                      appointmentDetails.remotePickupTimeSlotId = appointmentForm.availabilityForm.availability.timeSlotId;
                      appointmentDetails.timeSlotId = availability.data.availableDates[i].timeSlots[j].remoteAppointmentTimeSlotId;
                      break;
                    }
                  case 'DELIVERY':
                    {
                      appointmentDetails.timeSlotId = appointmentForm.availabilityForm.availability.timeSlotId;
                      appointmentDetails.remoteDropTimeSlotId = availability.data.availableDates[i].timeSlots[j].remoteDropTimeSlotId;
                      break;
                    }
                  case 'REMOTE':
                    {
                      appointmentDetails.remotePickupTimeSlotId = appointmentForm.availabilityForm.availability.timeSlotId;
                      appointmentDetails.timeSlotId = availability.data.availableDates[i].timeSlots[j].remoteAppointmentTimeSlotId;
                      appointmentDetails.remoteDropTimeSlotId = availability.data.availableDates[i].timeSlots[j].remoteDropTimeSlotId;
                      break;
                    }
                    default : break;
                }


              }
            }
          }
        }
      }

    }
    else {
      appointmentDetails.timeSlotId = appointmentForm.availabilityForm.availability.timeSlotId;
    }
    const vehicleForm = serviceAppointmentFeature.appointmentForm.editDetails?.appointment?.vehicle ? { ...serviceAppointmentFeature.appointmentForm.editDetails?.appointment?.vehicle, mileage: serviceAppointmentFeature.appointmentForm.vehicleForm.mileage } : {};
    const vehicle = serviceAppointmentFeature.appointmentForm.editDetails ? { ...serviceAppointmentFeature.appointmentForm.vehicleForm, ...vehicleForm } : serviceAppointmentFeature.appointmentForm.vehicleForm;
    appointmentDetails.vehicle = vehicleFormToVehicleProjector(vehicle);
    appointmentDetails.serviceRequests = this.buildSelectedServicesInfo(appointmentForm.serviceForm);
    /* Is Appt Eligible for CompRental code */
    let opCodeRentalEligible = this.isOpcodeCompRentalEligible.find(compEligible => compEligible);
    if (!opCodeRentalEligible && appointmentDetails.serviceRequests && appointmentDetails.serviceRequests.length > 0) {
      const totalCostAndLabourHrs = getTotalCostAndLabourHrs();
      let isCompRentalHrsElgible = false;
      let isCompRentalCostElgible = false;

      if (this.flatRateHrsForCompEligible && totalCostAndLabourHrs?.hrs) {
        //checking Complimentary rental eligible -flat rate hrs 
        const flatRateHrsForCompEligibleDuration = Duration.fromObject({ minutes: (parseInt(this.flatRateHrsForCompEligible.toString()) * 60) });
        const totalLabourHrsDuration = Duration.fromObject({ minutes: (parseInt(totalCostAndLabourHrs.hrs.toString()) * 60) });
        isCompRentalHrsElgible = totalLabourHrsDuration >= flatRateHrsForCompEligibleDuration;
      }
      if (totalCostAndLabourHrs?.cost && this.costForCompEligible) {
        //checking Complimentary rental eligible - cost 
        isCompRentalCostElgible = (totalCostAndLabourHrs.cost) >= parseInt(this.costForCompEligible.toString());
      }

      opCodeRentalEligible = isCompRentalCostElgible || isCompRentalHrsElgible;
    }
    appointmentDetails.dealershipApptComments = opCodeRentalEligible ? ': Eligible for Complimentary Rental - YES' : '';
    appointmentDetails.roundOffMileage = serviceAppointmentFeature?.serviceRequests?.data?.recommendationBasedOn?.roundedOffMileage;
    appointmentDetails.customer = this.getCustomerInfo(appointmentForm.customerForm);
    if (appointmentForm.customerForm.dropOffAddress.addressLine1 === '') {
      appointmentDetails.customer.isDropOffAddress = true;
    }
    else {
      appointmentDetails.customer.isDropOffAddress = false;
    }
    if (appointmentForm.customerForm.isRemoteLocation && appointmentForm.customerForm.pickUpAddress.addressLine1
      && appointmentForm.customerForm.dropOffAddress.addressLine1) {
      appointmentDetails.pickUpAddress = this.mapAddress(appointmentForm.customerForm.pickUpAddress);
      appointmentDetails.dropOffAddress = this.mapAddress(appointmentForm.customerForm.dropOffAddress);
    }
    return appointmentDetails;
  }

  getMainFormDataAsUpdateReq(serviceAppointmentFeature: ServiceAppointmentState): IServiceAppointment {
    const fullData = this.getMainFormDataAsCreateReq(serviceAppointmentFeature);
    fullData.appointmentId = serviceAppointmentFeature.appointmentForm.editDetails.appointmentId;
    fullData.dealerCode = serviceAppointmentFeature.appointmentForm.editDetails.appointment.dealerCode;
    return fullData;
  }

  checkIfRecallServiceSelected(serviceForm: IServiceRequestForm): boolean {
    return serviceForm.selectedServices.filter(t => t.details?.recommended?.isSSCRecall).length > 0;
  }

  buildSelectedServicesInfo(serviceForm: IServiceRequestForm): IServiceRequest[] {
    this.isOpcodeCompRentalEligible = [];
    return serviceForm.selectedServices.map(t => {
      this.isOpcodeCompRentalEligible.push(t.details.isCompRentalEligible);
      const opCode=t.details?.isInternalCode ? t.details?.mapDmsCode.opcode :t.opcode;
      const internalCode=t.details?.isInternalCode ? t.details?.opcode :"";
      const dmsApptOpcode = t.details?.isDmsApptOpcode ? t.details?.isDmsApptOpcode : false;
      return { opcode: opCode, comments: t.comments ,internalCode,subComplaints:t?.subComplaints, dmsApptOpcode: dmsApptOpcode} as IServiceRequest;
    });
  }

  getCustomerInfo(customerForm: ICustomerForm): ICustomer {
    const customer = {} as ICustomer;
    customer.firstName = customerForm.firstName;
    customer.lastName = customerForm.lastName;
    customer.emailAddress = customerForm.email;
    if(!customer.emailAddress) {
      delete customer.emailAddress;
    }
    customer.phoneNumber = customerForm.phoneNumber || '';
    if (customerForm.customerAddress?.addressLine1) {
      const address = {} as IAddress;
      address.addressLine1 = customerForm.customerAddress.addressLine1;
      address.addressLine2 = customerForm.customerAddress.addressLine2;
      address.city = customerForm.customerAddress.city;
      address.state = customerForm.customerAddress.state;
      address.zip = customerForm.customerAddress.zip;
      address.country = countryCd; // Hard coded for phase 1
      customer.address = address;
    }
    customer.customerType = customerForm.customerType ===  CustomerType.BUSINESS ? CustomerType.BUSINESS : CustomerType.INDIVIDUAL;
    customer.businessName = customerForm.businessName || '';
    return customer;
  }

  markStepAsTouched(step) {
    switch (step) {
      case 0:
        if (this.selectVehicleForm) {
          this.selectVehicleForm.markAllAsTouched();
        }
        return;
      case 1: {
        this.validateServices.next();
        return;
      }
      case 2:
        if (this.appointmentTimeForm) {
          this.appointmentTimeForm.markAllAsTouched();
        }
        return;
      case 3:
        if (this.yourInformationForm) {
          this.yourInformationForm.markAllAsTouched();
        }
        return;
      default:
        return false;
    }
  }

  private getSelectedVehicleSummary(vehicleForm: IVehicleForm) {
    const selectedVehicle = vehicleFormToVehicleProjector(vehicleForm);
    return `${selectedVehicle.year || ''} ${selectedVehicle.model} \n ${selectedVehicle.vin || ''}`;
  }
  private mapAddress(customerAddress) {
    const address = {} as IAddress;
    address.addressLine1 = customerAddress.addressLine1;
    address.addressLine2 = customerAddress.addressLine2;
    address.city = customerAddress.city;
    address.state = customerAddress.state;
    address.zip = customerAddress.zip;
    address.country = countryCd; //
    return address;
  }

  public setCostAndFlatRateHrsForCompEligibleRental(cost: string, flatRateHrs: string) {
    this.costForCompEligible = cost;
    this.flatRateHrsForCompEligible = flatRateHrs;
  }
}
